import { assetUrl } from "@/i18n/assets";
import { Link } from "@/i18n/Link";
import { ArrowLeft, BadgePercent, CheckCircle2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { RebateRedeemDialog } from "@/components/site/RebateRedeemDialog";
import { SITE_PHONE_DISPLAY } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    trail: [
      { name: "Home", path: "/" },
      { name: "Rebates", path: "/rebates/" },
      { name: "Axle Rebate", path: "/rebates/axle-rebate/" },
    ],
    seoTitle: "Trailer Axle Rebate - Up to 10,000 lb | Elkhart, IN",
    seoDesc: "Customer rebate on qualifying new trailer axles up to 10,000 lb at Axle Inc., Elkhart, IN. Honors prior estimates, no quantity limit. Expires July 7, 2026.",
    currentOffer: "Current Offer",
    title: "Axle Rebate on New Purchases",
    subtitle: "Save on qualifying new axle purchases up to 10,000 lb - direct rebate, processed locally.",
    imgAlt: "Axle Rebate - General Duty, Built to Work, From 3.5K to 10K",
    detailsTitle: "Offer details",
    detailsBody: "Customer rebate program returns money on qualifying axle and brake assembly purchases. Eligibility, amounts, and end dates rotate quarterly. Call the shop for the current schedule that applies to your purchase, some restrictions may apply - contact Axle Inc. with any questions.",
    finePrint: "The fine print",
    finePrintList: [
      "Offer good on new axle purchases up to 10,000 lb.",
      "Available on prior estimates, but not prior purchases.",
      "Not limited to quantity - rebate applies to each qualifying axle.",
      "Expires July 7, 2026. Receipt date must be on or before that date."
    ],
    redeem: "Redeem now",
    questions: (n: string) => `Questions? ${n}`,
    howItWorks: "How it works",
    steps: [
      "Buy a qualifying axle from Axle Inc. (or hand us a prior estimate before paying).",
      "Click Redeem now, fill in the short form, and upload your receipt.",
      "Our accounting team processes the rebate and follows up by email."
    ],
    allRebates: "All rebates & promotions"
  },
  es: {
    trail: [
      { name: "Inicio", path: "/" },
      { name: "Reembolsos", path: "/rebates/" },
      { name: "Reembolso de ejes", path: "/rebates/axle-rebate/" },
    ],
    seoTitle: "Reembolso de ejes para remolque - Hasta 10,000 lb | Elkhart, IN",
    seoDesc: "Reembolso para clientes en compras calificadas de ejes nuevos para remolque de hasta 10,000 lb en Axle Inc., Elkhart, IN. Aceptamos presupuestos previos, sin límite de cantidad. Vence el 7 de julio de 2026.",
    currentOffer: "Oferta actual",
    title: "Reembolso de ejes en compras nuevas",
    subtitle: "Ahorre en compras de ejes nuevos que califiquen de hasta 10,000 lb - reembolso directo, procesado localmente.",
    imgAlt: "Reembolso de ejes - Uso general, Construido para trabajar, De 3.5K a 10K",
    detailsTitle: "Detalles de la oferta",
    detailsBody: "El programa de reembolso para clientes devuelve dinero en compras calificadas de ejes y conjuntos de frenos. La elegibilidad, los montos y las fechas de finalización rotan trimestralmente. Llame al taller para conocer el calendario actual aplicable a su compra, pueden aplicar algunas restricciones - contacte a Axle Inc. si tiene alguna pregunta.",
    finePrint: "Letra pequeña",
    finePrintList: [
      "Oferta válida en compras de ejes nuevos de hasta 10,000 lb.",
      "Disponible en presupuestos previos, pero no en compras anteriores.",
      "Sin límite de cantidad - el reembolso aplica a cada eje que califique.",
      "Vence el 7 de julio de 2026. La fecha del recibo debe ser igual o anterior a esa fecha."
    ],
    redeem: "Canjear ahora",
    questions: (n: string) => `¿Preguntas? ${n}`,
    howItWorks: "Cómo funciona",
    steps: [
      "Compre un eje que califique en Axle Inc. (o entregue un presupuesto previo antes de pagar).",
      "Haga clic en Canjear ahora, complete el formulario y suba su recibo.",
      "Nuestro equipo de contabilidad procesa el reembolso y le contactará por correo electrónico."
    ],
    allRebates: "Todos los reembolsos y promociones"
  }
};

export default function AxleRebate() {
  const t = useCopy(COPY);
  const trail = t.trail;
  
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/rebates/axle-rebate/"
        jsonLd={[organizationJsonLd(), breadcrumbJsonLd(trail)]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-10 md:py-14">
          <div className="mb-4 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold inline-flex items-center gap-2">
            <BadgePercent className="h-4 w-4" /> {t.currentOffer}
          </p>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.title}
          </h1>
          <p className="mt-4 text-lg text-secondary-foreground/80 max-w-2xl">
            {t.subtitle}
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10 md:py-14 max-w-5xl">
        <div className="rounded-lg overflow-hidden border border-border shadow-sm">
          <picture>
            <source srcSet={assetUrl("/banners/axle-rebate.webp")} type="image/webp" />
            <img
              src={assetUrl("/banners/axle-rebate.webp")}
              alt={t.imgAlt}
              width={1200}
              height={628}
              className="w-full h-auto block"
            />
          </picture>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h2 className="text-2xl font-semibold tracking-tight">{t.detailsTitle}</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              {t.detailsBody}
            </p>

            <div className="mt-6 rounded-md border border-border bg-card p-5">
              <h3 className="text-sm uppercase tracking-wider text-primary-strong font-semibold">{t.finePrint}</h3>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                {t.finePrintList.map((item, i) => (
                  <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {item}</li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <RebateRedeemDialog>
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {t.redeem}
                </Button>
              </RebateRedeemDialog>
              <Button size="lg" variant="outline" asChild>
                <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                  <Phone className="mr-1.5 h-4 w-4" /> {t.questions(SITE_PHONE_DISPLAY)}
                </a>
              </Button>
            </div>
          </div>

          <aside className="md:border-l md:border-border md:pl-8">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-semibold">{t.howItWorks}</h3>
            <ol className="mt-3 space-y-4 text-sm">
              {t.steps.map((step, i) => (
                <li key={i}><span className="font-semibold text-primary-strong">{i + 1}.</span> {step}</li>
              ))}
            </ol>
            <Link to="/rebates/" className="mt-6 inline-flex items-center text-sm text-primary-strong hover:underline">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> {t.allRebates}
            </Link>
          </aside>
        </div>
      </section>
    </PageLayout>
  );
}
